import localeExtensions from "./localeExtensions.json";

export const SUPPORTED_LOCALES = ["de", "en", "fr", "it", "es", "pt", "hi"];
export const DEFAULT_LOCALE = "de";
export const INDEXED_LOCALES = SUPPORTED_LOCALES.filter(
  (locale) => locale !== DEFAULT_LOCALE
);

export const HREFLANG_BY_LOCALE = {
  de: "de-CH",
  en: "en",
  fr: "fr",
  it: "it",
  es: "es",
  pt: "pt-BR",
  hi: "hi",
};

export const LOCALE_OPTIONS = [
  { code: "de", name: "Deutsch" },
  { code: "en", name: "English" },
  { code: "fr", name: "Français" },
  { code: "it", name: "Italiano" },
  { code: "es", name: "Español" },
  { code: "pt", name: "Português" },
  { code: "hi", name: "हिन्दी" },
];

const CATEGORY_SLUGS = {
  soccer: {
    de: "fussball",
    en: "soccer",
    fr: "football",
    it: "calcio",
    es: "futbol",
    pt: "futebol",
    hi: "soccer",
  },
};

function findCategoryTagBySlug(slug) {
  return Object.entries(CATEGORY_SLUGS).find(([, slugs]) =>
    Object.values(slugs).includes(slug)
  )?.[0];
}

function localizeKnownCategoryPath(path, locale) {
  const segments = path.split("/").filter(Boolean);

  if (segments.length !== 1) {
    return path;
  }

  const categoryTag = findCategoryTagBySlug(segments[0]);
  const localizedSlug = categoryTag ? CATEGORY_SLUGS[categoryTag]?.[locale] : null;

  return localizedSlug ? `/${localizedSlug}` : path;
}

const BASE_DICTIONARY = {
  de: {
    siteTagline: "Kostenlose Malvorlagen mit Stil",
    navGallery: "Galerie",
    navTopics: "Themen",
    navPrint: "Drucken",
    navFaq: "FAQ",
    languageLabel: "Sprache",
    footerCopy: "Kostenlose Ausmalbilder und Malvorlagen zum Ausdrucken.",
    footerSubcopy:
      "Tiere, Dinosaurier, Einhörner, Natur, Feiertage und saisonale Motive als druckfreundliche PNG-Dateien.",
    footerAnimals: "Tiere",
    footerDinosaurs: "Dinosaurier",
    footerUnicorn: "Einhorn",
    footerEaster: "Ostern",
    footerAbout: "Über",
    footerHowMade: "Entstehung",
    footerLicense: "Nutzung",
    footerPrintTips: "Drucktipps",
    footerPrivacy: "Datenschutz",
    privacySettings: "Cookie-Einstellungen",
    homeTitle: "Ausmalbilder kostenlos ausdrucken",
    homePageTitle:
      "Ausmalbilder kostenlos ausdrucken - gratis Malvorlagen | usmalbilder.ch",
    categoryPageTitle: "{category} Ausmalbilder kostenlos ausdrucken | usmalbilder.ch",
    categoryMissingTitle: "Kategorie nicht gefunden | usmalbilder.ch",
    homeDescription:
      "Über 2.000 kostenlose Ausmalbilder und Malvorlagen für Kinder: Tiere, Dinosaurier, Einhörner, Jahreszeiten und mehr als PNG oder PDF ausdrucken.",
    categoryDescription:
      "{category} Ausmalbilder kostenlos ausdrucken: hochwertige Malvorlagen mit klaren Konturen, schneller Vorschau und direkt druckbaren PNG-Dateien.",
    categoryMissingDescription: "Diese Ausmalbilder-Kategorie wurde nicht gefunden.",
    homeIntro:
      "Kostenlose Ausmalbilder direkt ausdrucken - ohne Anmeldung und ohne Umwege. Öffne dein Lieblingsmotiv als A4-PNG oder PDF und leg sofort los. Alle Vorlagen sind mit klaren schwarzen Linien, hellem Hintergrund und gut ausmalbaren Flächen für Zuhause, Kindergarten und Schule vorbereitet.",
    categoryIntro:
      "{category} Ausmalbilder sind ideal, wenn du schnell ein fertiges Motiv für Zuhause, Kindergarten, Schule oder eine ruhige kreative Pause brauchst. Alle Vorlagen haben eine Vorschau, eine eigene Detailseite und eine direkt zugängliche PNG-Datei.",
    searchLabel: "Ausmalbild suchen",
    searchAria: "Ausmalbilder suchen",
    searchPlaceholder: "Einhorn, Dino, Ostern, Rakete...",
    searchHintsAria: "Beliebte Suchbegriffe",
    heroPromisesAria: "Vorteile der Malvorlagen",
    heroShowcaseAria: "Beispiel Ausmalbilder",
    statsAria: "Galerie Kennzahlen",
    statImages: "Ausmalbilder",
    statPremium: "neue Premium-Motive",
    statCategories: "Themenwelten",
    statPrint: "PNG zum Drucken",
    all: "Alle",
    featuredTopics: "Beliebte Themen",
    ageGroupsTitle: "Ausmalbilder nach Alter",
    ageGroupsText:
      "Wähle eine Sammlung mit einem Detailgrad, der gut zum Alter und zur Lust aufs Ausmalen passt.",
    ageGroupYoungTitle: "2-4 Jahre",
    ageGroupYoungText: "Klare Tiere mit gut erkennbaren Formen für den einfachen Einstieg.",
    ageGroupMiddleTitle: "4-6 Jahre",
    ageGroupMiddleText: "Vertraute Fantasiefiguren und kleine Szenen für Kindergarten und Vorschule.",
    ageGroupOlderTitle: "7+ Jahre",
    ageGroupOlderText: "Muster und detailreichere Motive für längere kreative Pausen.",
    newestImagesTitle: "Neu hinzugefügte Ausmalbilder",
    newestImagesText: "Frische, druckfreundliche Motive aus den zuletzt erweiterten Sammlungen.",
    categoriesTitle: "Ausmalbilder nach Thema",
    popularImages: "Beliebte Ausmalbilder",
    topicHubsTitle: "Themen-Hubs für beliebte Suchanfragen",
    topicHubsText:
      "Direkte Einstiege für starke Suchbegriffe wie Ausmalbilder kostenlos, Malvorlagen zum Ausdrucken und Einhorn Ausmalbilder.",
    pdfPacksTitle: "PDF-Pakete zum Ausdrucken",
    pdfPacksText:
      "Lade mehrere passende Ausmalbilder als A4-PDF herunter. Die Pakete werden automatisch aus den Bilddaten erzeugt.",
    downloadPdfPack: "PDF herunterladen",
    topicCount: "{count} Ausmalbilder",
    allImages: "Alle Ausmalbilder",
    total: "{count} gesamt",
    galleryErrorTitle: "Die Galerie lässt sich gerade nicht laden.",
    galleryErrorText:
      "Bitte lade die Seite erneut. Wenn das Problem bleibt, prüfe, ob public/db.json erzeugt wurde.",
    missingCategoryTitle: "Diese Kategorie existiert nicht.",
    noSearchTitle: "Keine Treffer für diese Suche.",
    emptyTitle: "Noch keine Bilder in dieser Sammlung.",
    missingCategoryText: "Bitte wähle eine vorhandene Kategorie aus der Galerie.",
    noSearchText:
      "Versuche einen kürzeren Suchbegriff oder wechsle in eine andere Kategorie.",
    emptyText:
      "Wechsle zu einer anderen Kategorie oder schau später wieder vorbei, wenn neue Motive hinzugekommen sind.",
    openPreview: "Vorschau öffnen",
    loadMore: "Weitere {count} Motive laden",
    visibleCount: "{visible} von {total} sichtbar",
    beforePrint: "Vor dem Drucken",
    beforePrintText1:
      "Gute Malvorlagen haben starke Konturen, genug Abstand zwischen den Linien und Motive, die Kinder sofort erkennen.",
    beforePrintText2:
      "Jede Detailseite zeigt eine Vorschau und führt direkt zur großen PNG-Datei für A4-Ausdrucke.",
    printTips: "Drucktipps",
    printTip1Title: "Motiv passend wählen",
    printTip1Text:
      "Kleine Kinder: große Flächen. Ältere Kinder: Szenen, Muster und detailreichere Motive.",
    printTip2Title: "Auf A4 drucken",
    printTip2Text:
      "Öffne die PNG-Datei, wähle Hochformat und drucke mit schwarzer Tinte.",
    printTip3Title: "Mehrere Seiten kombinieren",
    printTip3Text:
      "Für Schule, Geburtstag oder Ferien lohnt sich eine kleine Themenmappe.",
    relatedSearchesTitle: "Gesuchte Malvorlagen",
    questions: "Fragen",
    faqFreeQuestion: "Sind die Ausmalbilder kostenlos?",
    faqFreeAnswer:
      "Ja. Die Motive auf usmalbilder.ch können kostenlos geöffnet und ausgedruckt werden. Jede Detailseite führt direkt zur druckbaren PNG-Datei.",
    faqKidsQuestion: "Welche Ausmalbilder eignen sich für Kinder?",
    faqKidsAnswer:
      "Besonders gut eignen sich Motive mit klaren Konturen, großen Flächen und wenig feinen Details. In der Galerie findest du einfache Tiere, Einhörner, Dinosaurier, Naturmotive und saisonale Vorlagen.",
    faqPrintQuestion: "Wie drucke ich ein Ausmalbild am besten?",
    faqPrintAnswer:
      "Öffne ein Motiv, wähle die Originaldatei und drucke sie auf A4 im Hochformat. Für saubere Linien empfiehlt sich die Einstellung 'An Seite anpassen' oder eine Skalierung nahe 100 Prozent.",
    faqTopicsQuestion: "Welche Themen gibt es?",
    faqTopicsAnswer:
      "Die Sammlung enthält Ausmalbilder zu Einhorn, Prinzessin, Dinosaurier, Tieren, Mandalas, Weltraum, Natur, Essen, Sommer, Ostern, Weihnachten, Halloween und Fantasie.",
    faqNoLoginQuestion: "Kann ich die Ausmalbilder ohne Anmeldung direkt drucken?",
    faqNoLoginAnswer:
      "Ja. Öffne ein Motiv in der Galerie, rufe die Detailseite auf und öffne die druckbare Datei direkt - ohne Konto und ohne Anmeldung.",
    faqFormatsQuestion: "In welchem Format sind die Ausmalbilder verfügbar?",
    faqFormatsAnswer:
      "Jede Detailseite führt zu einer druckfreundlichen PNG-Datei im A4-Hochformat. Für viele Themen gibt es zusätzlich PDF-Pakete mit mehreren passenden Motiven.",
    faqPdfPacksQuestion: "Gibt es PDF-Pakete für mehrere Ausmalbilder?",
    faqPdfPacksAnswer:
      "Ja. Auf der Startseite und auf vielen Themenseiten findest du PDF-Pakete, die mehrere passende A4-Ausmalbilder in einer Datei bündeln.",
    detailLoadingTitle: "Bild wird vorbereitet",
    detailLoadingText:
      "Die Detailansicht lädt gerade die Bilddaten und richtet die Druckansicht ein.",
    detailErrorTitle: "Diese Detailseite ist gerade nicht verfügbar.",
    detailErrorText: "Bitte lade die Seite erneut oder kehre zur Galerie zurück.",
    detailMissingTitle: "Dieses Ausmalbild wurde nicht gefunden.",
    detailMissingText:
      "Eventuell wurde die Datei verschoben oder die Galerie muss neu vorbereitet werden.",
    imageMissingPageTitle: "Ausmalbild nicht gefunden | usmalbilder.ch",
    imageLoadingPageTitle: "Ausmalbild wird geladen | usmalbilder.ch",
    imagePageTitle: "{title} kostenlos auf A4 ausdrucken | usmalbilder.ch",
    topicHubPageTitle: "{title} | kostenlose Malvorlagen | usmalbilder.ch",
    imagePageDescription:
      "{description} Dieses kostenlose Ausmalbild als PNG öffnen, auf A4 drucken und als Malvorlage für Kinder nutzen.",
    freeColoringPages: "Kostenlose Ausmalbilder und Malvorlagen auf usmalbilder.ch.",
    backToGallery: "Zur Galerie",
    free: "Kostenlos",
    instantPrint: "Direkt druckbar",
    noLogin: "Ohne Anmeldung",
    printFormats: "A4 PNG & PDF",
    openColoringPage: "Jetzt Ausmalbild öffnen",
    moreFromCollection: "Mehr aus dieser Sammlung",
    imageDetails: "Bilddetails",
    asFreeTemplate: "{title} als kostenlose Malvorlage",
    detailText:
      "Dieses {category} Ausmalbild ist als druckfreundliche PNG-Datei vorbereitet. Öffne die Vorlage, drucke sie auf A4 und nutze sie zuhause, im Kindergarten oder in der Schule.",
    category: "Kategorie",
    file: "Datei",
    use: "Einsatz",
    useValue: "Zuhause, Schule oder kreative Pause",
    sameCollection: "Aus derselben Sammlung",
    imageRelatedTopicsTitle: "Passende Themen",
    curatedImagesTitle: "Empfohlene Motive",
    ageGuideTitle: "Altersempfehlung",
    difficultyGuideTitle: "Schwierigkeitsgrad",
    printGuideTitle: "Druckempfehlung",
    bestForTitle: "Besonders geeignet für",
    imageSupportTitle: "Drucken und nutzen",
    imageSupportText:
      "Mehr Hintergrund zur Erstellung, Nutzung und zum sauberen A4-Druck dieser Malvorlagen.",
    imageHowMadeLink: "So entstehen die Ausmalbilder",
    imageLicenseLink: "Nutzung und Lizenz",
    imagePrintTipsLink: "Drucktipps für A4",
    imageKeywordColoringPage: "Ausmalbild",
    imageKeywordPrintable: "Malvorlage zum Ausdrucken",
    notFoundTitle: "Diese Seite existiert nicht.",
    notFoundText:
      "Der Link ist wahrscheinlich veraltet oder die Route wurde verschoben. Über die Galerie kommst du schnell wieder zu den Ausmalbildern.",
    aboutTitle: "Über usmalbilder.ch",
    aboutMetaTitle: "Über das Projekt | usmalbilder.ch",
    aboutDescription:
      "Mehr über das Familienprojekt hinter usmalbilder.ch und den Einsatz von KI für moderne Ausmalbilder.",
    aboutBreadcrumb: "Über das Projekt",
    aboutEyebrow: "Hinter dem Projekt",
    aboutHeroText:
      "usmalbilder.ch ist ein Familienprojekt rund um moderne, druckfreundliche Ausmalbilder. Die Motive entstehen mit KI-Unterstützung und werden anschließend für die Website vorbereitet, sortiert und veröffentlicht.",
    aboutPrimary: "Ausmalbilder finden",
    aboutSecondary: "Mehr zum Projekt",
    aboutLead:
      "Die Idee ist bewusst einfach: gute Malvorlagen sollen schnell gefunden werden können, ohne unübersichtliche Kataloge, aggressive Werbung oder komplizierte Downloads.",
    aboutParagraph1:
      "Generative KI wird hier als Werkzeug eingesetzt, um neue Illustrationen für Kinder und Erwachsene zu entwickeln. Danach werden die Bilder kuratiert, mit Titeln und Beschreibungen versehen, in Sammlungen einsortiert und für den Druck vorbereitet.",
    aboutParagraph2:
      "Gleichzeitig ist das Projekt auch eine praktische Lernerfahrung: Wie kann man digitale Werkzeuge sinnvoll nutzen, wie baut man ein eigenes Online-Angebot auf und wie entsteht daraus ein kleines Familiengeschäft?",
    aboutParagraph3:
      "Wenn du mehr über den Hintergrund erfahren willst, findest du weitere Informationen auf christianmenz.ch.",
    aboutPrinciple1Label: "Idee",
    aboutPrinciple1Title: "Ein kleines Familienprojekt",
    aboutPrinciple1Text:
      "Die Seite verbindet Kreativität, digitale Werkzeuge und die Freude daran, etwas Eigenes aufzubauen.",
    aboutPrinciple2Label: "Ansatz",
    aboutPrinciple2Title: "KI als Werkzeug, nicht als Selbstzweck",
    aboutPrinciple2Text:
      "Generative Modelle helfen beim Erstellen neuer Motive, die anschließend kuratiert und auf die Seite gebracht werden.",
    aboutPrinciple3Label: "Ziel",
    aboutPrinciple3Title: "Einfach nutzbar für Eltern und Kinder",
    aboutPrinciple3Text:
      "Die Malvorlagen sollen schnell gefunden, direkt geöffnet und ohne Umwege ausgedruckt werden können.",
    whyTitle: "Warum Ausmalbilder gut sind",
    whyMetaTitle: "Warum Ausmalbilder gut sind | usmalbilder.ch",
    whyDescription:
      "Warum Ausmalbilder Fokus, Entspannung und kreativen Ausdruck für Kinder und Erwachsene fördern.",
    whyEyebrow: "Kreative Pause",
    whyHeroText:
      "Ausmalen ist mehr als nur Beschäftigung. Es schafft Fokus, Entspannung und einen einfachen Zugang zu kreativem Ausdruck für Kinder und Erwachsene.",
    whyLead:
      "Die positiven Auswirkungen des Ausmalens auf das Wohlbefinden sind erstaunlich vielseitig. Schon wenige Minuten können helfen, den Alltag kurz auszublenden und den Kopf neu zu sortieren.",
    whyParagraph1:
      "Ausmalen verbindet eine klare Aufgabe mit viel Freiraum. Das macht es leicht, direkt loszulegen, ohne Druck und ohne das Gefühl, etwas leisten zu müssen.",
    whyParagraph2:
      "Gerade für Kinder ist das wertvoll, weil sie spielerisch Farbe, Formen und Konzentration erleben. Erwachsene profitieren oft von der ruhigen, fast meditativen Wiederholung.",
    whyInfoLabel: "Kurz gesagt",
    whyInfoTitle: "Weniger Reiz, mehr Ruhe",
    whyInfoText:
      "Malvorlagen bieten einen einfachen Einstieg in eine konzentrierte Pause, ganz ohne Vorbereitung.",
    benefit1Title: "Fokus und Konzentration",
    benefit1Text:
      "Beim Ausmalen wird Aufmerksamkeit gebündelt. Das hilft Kindern und Erwachsenen, für einen Moment bewusst bei einer Sache zu bleiben.",
    benefit2Title: "Stressabbau",
    benefit2Text:
      "Ruhige, wiederholende Handbewegungen schaffen eine entspannte Routine und können das Gedankenkarussell verlangsamen.",
    benefit3Title: "Kreativer Ausdruck",
    benefit3Text:
      "Farben, Kontraste und eigene Entscheidungen machen aus jeder Vorlage ein persönliches kleines Kunstwerk.",
    benefit4Title: "Achtsame Pause",
    benefit4Text:
      "Ausmalen schafft einen Gegenpol zu schnellen Bildschirmen und fühlt sich oft wie eine kleine kreative Auszeit an.",
  },
  en: {
    siteTagline: "Free coloring pages with style",
    navGallery: "Gallery",
    navTopics: "Topics",
    navPrint: "Print",
    navFaq: "FAQ",
    languageLabel: "Language",
    footerCopy: "Free coloring pages and printable sheets.",
    footerSubcopy:
      "Animals, dinosaurs, unicorns, nature, holidays and seasonal motifs as print-friendly PNG files.",
    footerAnimals: "Animals",
    footerDinosaurs: "Dinosaurs",
    footerUnicorn: "Unicorn",
    footerEaster: "Easter",
    footerAbout: "About",
    footerHowMade: "How made",
    footerLicense: "Usage",
    footerPrintTips: "Print tips",
    footerPrivacy: "Privacy",
    privacySettings: "Cookie settings",
    homeTitle: "Free coloring pages to print",
    homePageTitle: "Free coloring pages to print | usmalbilder.ch",
    categoryPageTitle: "{category} coloring pages to print | usmalbilder.ch",
    categoryMissingTitle: "Category not found | usmalbilder.ch",
    homeDescription:
      "More than 2,000 free coloring pages for kids: animals, dinosaurs, unicorns, seasons and more to print as PNG or PDF.",
    categoryDescription:
      "{category} coloring pages to print: high-quality printable sheets with clear outlines, fast previews and direct PNG downloads.",
    categoryMissingDescription: "This coloring page category was not found.",
    homeIntro:
      "Print free coloring pages directly - no login and no detours. Open your favorite motif as an A4 PNG or PDF and start right away. Every sheet is prepared with clear black lines, a light background and easy-to-color spaces for home, preschool and school.",
    categoryIntro:
      "{category} coloring pages are ideal when you need a ready-to-print motif for home, kindergarten, school or a quiet creative break.",
    searchLabel: "Search coloring pages",
    searchAria: "Search coloring pages",
    searchPlaceholder: "Unicorn, dino, Easter, rocket...",
    searchHintsAria: "Popular searches",
    heroPromisesAria: "Coloring page benefits",
    heroShowcaseAria: "Example coloring pages",
    statsAria: "Gallery stats",
    statImages: "coloring pages",
    statPremium: "new premium motifs",
    statCategories: "topic worlds",
    statPrint: "PNG for printing",
    all: "All",
    featuredTopics: "Popular topics",
    ageGroupsTitle: "Coloring pages by age",
    ageGroupsText:
      "Choose a collection with a level of detail that fits the child's age and interest.",
    ageGroupYoungTitle: "Ages 2-4",
    ageGroupYoungText: "Clear animal motifs with easy-to-recognize shapes for a first start.",
    ageGroupMiddleTitle: "Ages 4-6",
    ageGroupMiddleText: "Familiar fantasy characters and small scenes for preschool and kindergarten.",
    ageGroupOlderTitle: "Ages 7+",
    ageGroupOlderText: "Patterns and more detailed motifs for longer creative breaks.",
    newestImagesTitle: "Newly added coloring pages",
    newestImagesText: "Fresh print-friendly motifs from the collections we expanded most recently.",
    categoriesTitle: "Coloring pages by topic",
    popularImages: "Popular coloring pages",
    topicHubsTitle: "Topic hubs for popular searches",
    topicHubsText:
      "Direct entry points for searches such as free coloring pages, printable sheets and unicorn coloring pages.",
    pdfPacksTitle: "PDF packs to print",
    pdfPacksText:
      "Download several matching coloring pages as an A4 PDF. The packs are generated automatically from the image data.",
    downloadPdfPack: "Download PDF",
    topicCount: "{count} coloring pages",
    allImages: "All coloring pages",
    total: "{count} total",
    galleryErrorTitle: "The gallery cannot be loaded right now.",
    galleryErrorText:
      "Please reload the page. If the problem remains, check whether public/db.json was generated.",
    missingCategoryTitle: "This category does not exist.",
    noSearchTitle: "No matches for this search.",
    emptyTitle: "No images in this collection yet.",
    missingCategoryText: "Please choose an existing category from the gallery.",
    noSearchText: "Try a shorter search term or switch to another category.",
    emptyText: "Switch to another category or come back later for new motifs.",
    openPreview: "Open preview",
    loadMore: "Load {count} more motifs",
    visibleCount: "{visible} of {total} visible",
    beforePrint: "Before printing",
    beforePrintText1:
      "Good coloring sheets have strong outlines, enough spacing between lines and motifs children recognize immediately.",
    beforePrintText2:
      "Every detail page shows a preview and links directly to the large PNG file for A4 prints.",
    printTips: "Print tips",
    printTip1Title: "Choose the right motif",
    printTip1Text:
      "Younger children: large areas. Older children: scenes, patterns and more detailed motifs.",
    printTip2Title: "Print on A4",
    printTip2Text: "Open the PNG file, choose portrait format and print with black ink.",
    printTip3Title: "Combine several pages",
    printTip3Text:
      "For school, birthdays or holidays, a small themed folder is useful.",
    relatedSearchesTitle: "Searched coloring sheets",
    questions: "Questions",
    faqFreeQuestion: "Are the coloring pages free?",
    faqFreeAnswer:
      "Yes. The motifs on usmalbilder.ch can be opened and printed for free. Every detail page links directly to the printable PNG file.",
    faqKidsQuestion: "Which coloring pages are good for kids?",
    faqKidsAnswer:
      "Motifs with clear outlines, large areas and fewer tiny details work especially well. The gallery includes simple animals, unicorns, dinosaurs, nature motifs and seasonal pages.",
    faqPrintQuestion: "How should I print a coloring page?",
    faqPrintAnswer:
      "Open a motif, choose the original file and print it on A4 in portrait format. For clean lines, use 'fit to page' or a scale close to 100 percent.",
    faqTopicsQuestion: "Which topics are available?",
    faqTopicsAnswer:
      "The collection includes unicorns, princesses, dinosaurs, animals, mandalas, space, nature, food, summer, Easter, Christmas, Halloween and fantasy.",
    faqNoLoginQuestion: "Can I print the coloring pages directly without signing in?",
    faqNoLoginAnswer:
      "Yes. Open a motif in the gallery, visit its detail page and open the printable file directly - no account or sign-in required.",
    faqFormatsQuestion: "Which file formats are available?",
    faqFormatsAnswer:
      "Every detail page links to a print-friendly PNG in portrait A4 format. Many topics also offer PDF packs with several matching motifs.",
    faqPdfPacksQuestion: "Are PDF packs available for several coloring pages?",
    faqPdfPacksAnswer:
      "Yes. The home page and many topic pages offer PDF packs that bundle several matching A4 coloring pages in one file.",
    detailLoadingTitle: "Preparing image",
    detailLoadingText: "The detail view is loading the image data and print view.",
    detailErrorTitle: "This detail page is not available right now.",
    detailErrorText: "Please reload the page or return to the gallery.",
    detailMissingTitle: "This coloring page was not found.",
    detailMissingText:
      "The file may have moved or the gallery needs to be prepared again.",
    imageMissingPageTitle: "Coloring page not found | usmalbilder.ch",
    imageLoadingPageTitle: "Coloring page loading | usmalbilder.ch",
    imagePageTitle: "{title} - free printable A4 | usmalbilder.ch",
    topicHubPageTitle: "{title} | free printable sheets | usmalbilder.ch",
    imagePageDescription:
      "{description} Open this free coloring page as a PNG, print it on A4 and use it as a printable sheet for kids.",
    freeColoringPages: "Free coloring pages and printable sheets on usmalbilder.ch.",
    backToGallery: "Back to gallery",
    free: "Free",
    instantPrint: "Print directly",
    noLogin: "No login",
    printFormats: "A4 PNG & PDF",
    openColoringPage: "Open coloring page",
    moreFromCollection: "More from this collection",
    imageDetails: "Image details",
    asFreeTemplate: "{title} as a free printable sheet",
    detailText:
      "This {category} coloring page is prepared as a print-friendly PNG file. Open it, print it on A4 and use it at home, kindergarten or school.",
    category: "Category",
    file: "File",
    use: "Use",
    useValue: "Home, school or creative break",
    sameCollection: "From the same collection",
    imageRelatedTopicsTitle: "Related topics",
    curatedImagesTitle: "Recommended pages",
    ageGuideTitle: "Age guidance",
    difficultyGuideTitle: "Difficulty",
    printGuideTitle: "Printing guidance",
    bestForTitle: "Especially useful for",
    imageSupportTitle: "Print and use",
    imageSupportText:
      "More background about creation, usage and clean A4 printing for these coloring sheets.",
    imageHowMadeLink: "How the coloring pages are made",
    imageLicenseLink: "Usage and license",
    imagePrintTipsLink: "A4 print tips",
    imageKeywordColoringPage: "coloring page",
    imageKeywordPrintable: "printable sheet",
    notFoundTitle: "This page does not exist.",
    notFoundText:
      "The link is probably outdated or the route moved. The gallery gets you back to the coloring pages quickly.",
    aboutTitle: "About usmalbilder.ch",
    aboutMetaTitle: "About the project | usmalbilder.ch",
    aboutDescription:
      "Learn more about the family project behind usmalbilder.ch and the use of AI for modern coloring pages.",
    aboutBreadcrumb: "About the project",
    aboutEyebrow: "Behind the project",
    aboutHeroText:
      "usmalbilder.ch is a family project for modern, print-friendly coloring pages. The motifs are created with AI support and then prepared, sorted and published for the website.",
    aboutPrimary: "Find coloring pages",
    aboutSecondary: "More about the project",
    aboutLead:
      "The idea is deliberately simple: good printable sheets should be easy to find, without confusing catalogs, aggressive ads or complicated downloads.",
    aboutParagraph1:
      "Generative AI is used here as a tool to develop new illustrations for children and adults. The images are then curated, given titles and descriptions, sorted into collections and prepared for printing.",
    aboutParagraph2:
      "At the same time, the project is a practical learning experience: how can digital tools be used well, how do you build your own online offer, and how can that become a small family business?",
    aboutParagraph3:
      "If you want to learn more about the background, you can find additional information on christianmenz.ch.",
    aboutPrinciple1Label: "Idea",
    aboutPrinciple1Title: "A small family project",
    aboutPrinciple1Text:
      "The site combines creativity, digital tools and the joy of building something of your own.",
    aboutPrinciple2Label: "Approach",
    aboutPrinciple2Title: "AI as a tool, not an end in itself",
    aboutPrinciple2Text:
      "Generative models help create new motifs that are then curated and brought onto the site.",
    aboutPrinciple3Label: "Goal",
    aboutPrinciple3Title: "Easy for parents and children",
    aboutPrinciple3Text:
      "The printable sheets should be quick to find, open directly and print without detours.",
    whyTitle: "Why coloring pages are good",
    whyMetaTitle: "Why coloring pages are good | usmalbilder.ch",
    whyDescription:
      "Why coloring pages support focus, relaxation and creative expression for children and adults.",
    whyEyebrow: "Creative break",
    whyHeroText:
      "Coloring is more than keeping busy. It creates focus, relaxation and an easy path into creative expression for children and adults.",
    whyLead:
      "The positive effects of coloring on wellbeing are surprisingly varied. Just a few minutes can help fade out daily noise and reset your mind.",
    whyParagraph1:
      "Coloring combines a clear task with plenty of freedom. That makes it easy to start without pressure and without feeling you have to perform.",
    whyParagraph2:
      "This is valuable for children because they experience color, shapes and concentration through play. Adults often benefit from the quiet, almost meditative repetition.",
    whyInfoLabel: "In short",
    whyInfoTitle: "Less noise, more calm",
    whyInfoText:
      "Printable sheets offer an easy entry into a focused break, with no preparation needed.",
    benefit1Title: "Focus and concentration",
    benefit1Text:
      "Coloring gathers attention. It helps children and adults stay with one thing for a moment.",
    benefit2Title: "Stress relief",
    benefit2Text:
      "Quiet, repeating hand movements create a relaxed routine and can slow down racing thoughts.",
    benefit3Title: "Creative expression",
    benefit3Text:
      "Colors, contrasts and personal choices turn every page into a small artwork.",
    benefit4Title: "Mindful pause",
    benefit4Text:
      "Coloring offers a counterpoint to fast screens and often feels like a small creative timeout.",
  },
  fr: {
    siteTagline: "Coloriages gratuits avec style",
    navGallery: "Galerie",
    navTopics: "Thèmes",
    navPrint: "Imprimer",
    navFaq: "FAQ",
    languageLabel: "Langue",
    footerCopy: "Coloriages gratuits et pages à imprimer.",
    footerSubcopy:
      "Animaux, dinosaures, licornes, nature, fêtes et motifs saisonniers en PNG faciles à imprimer.",
    footerAnimals: "Animaux",
    footerDinosaurs: "Dinosaures",
    footerUnicorn: "Licorne",
    footerEaster: "Pâques",
    footerAbout: "À propos",
    footerHowMade: "Création",
    footerLicense: "Utilisation",
    footerPrintTips: "Impression",
    footerPrivacy: "Confidentialité",
    privacySettings: "Paramètres des cookies",
    homeTitle: "Coloriages gratuits à imprimer",
    homePageTitle: "Coloriages gratuits à imprimer | usmalbilder.ch",
    categoryPageTitle: "Coloriages {category} à imprimer | usmalbilder.ch",
    categoryMissingTitle: "Catégorie introuvable | usmalbilder.ch",
    homeDescription:
      "Plus de 2 000 coloriages gratuits pour enfants : animaux, dinosaures, licornes, saisons et autres motifs à imprimer en PNG ou PDF.",
    categoryDescription:
      "Coloriages {category} à imprimer : pages de qualité avec contours nets, aperçu rapide et fichiers PNG directs.",
    categoryMissingDescription: "Cette catégorie de coloriages est introuvable.",
    homeIntro:
      "Imprime des coloriages gratuits directement, sans inscription ni détour. Ouvre ton motif préféré en PNG ou PDF A4 et commence tout de suite. Chaque page propose des lignes noires nettes, un fond clair et de grandes zones à colorier pour la maison, la maternelle et l’école.",
    categoryIntro:
      "Les coloriages {category} sont pratiques quand il faut vite un motif prêt à imprimer pour la maison, l’école, la maternelle ou une pause créative.",
    searchLabel: "Rechercher un coloriage",
    searchAria: "Rechercher des coloriages",
    searchPlaceholder: "Licorne, dino, Pâques, fusée...",
    searchHintsAria: "Recherches populaires",
    heroPromisesAria: "Avantages des coloriages",
    heroShowcaseAria: "Exemples de coloriages",
    statsAria: "Statistiques de la galerie",
    statImages: "coloriages",
    statPremium: "nouveaux motifs premium",
    statCategories: "univers thématiques",
    statPrint: "PNG à imprimer",
    all: "Tous",
    featuredTopics: "Thèmes populaires",
    ageGroupsTitle: "Coloriages par âge",
    ageGroupsText:
      "Choisis une collection avec un niveau de détail adapté à l'âge et à l'envie de colorier.",
    ageGroupYoungTitle: "2-4 ans",
    ageGroupYoungText: "Des animaux aux formes faciles à reconnaître pour bien commencer.",
    ageGroupMiddleTitle: "4-6 ans",
    ageGroupMiddleText: "Des personnages fantastiques familiers et de petites scènes pour la maternelle.",
    ageGroupOlderTitle: "7 ans et plus",
    ageGroupOlderText: "Des motifs et dessins plus détaillés pour de longues pauses créatives.",
    newestImagesTitle: "Coloriages ajoutés récemment",
    newestImagesText: "Des motifs faciles à imprimer issus des collections agrandies récemment.",
    categoriesTitle: "Coloriages par thème",
    popularImages: "Coloriages populaires",
    topicHubsTitle: "Pages thématiques pour recherches populaires",
    topicHubsText:
      "Des entrées directes pour coloriages gratuits, coloriages à imprimer et coloriages licorne.",
    pdfPacksTitle: "Packs PDF à imprimer",
    pdfPacksText:
      "Télécharge plusieurs coloriages assortis dans un PDF A4. Les packs sont générés automatiquement à partir des images.",
    downloadPdfPack: "Télécharger le PDF",
    topicCount: "{count} coloriages",
    allImages: "Tous les coloriages",
    total: "{count} au total",
    galleryErrorTitle: "La galerie ne peut pas être chargée pour le moment.",
    galleryErrorText:
      "Recharge la page. Si le problème persiste, vérifie que public/db.json a été généré.",
    missingCategoryTitle: "Cette catégorie n’existe pas.",
    noSearchTitle: "Aucun résultat pour cette recherche.",
    emptyTitle: "Aucune image dans cette collection pour l’instant.",
    missingCategoryText: "Choisis une catégorie existante dans la galerie.",
    noSearchText: "Essaie un terme plus court ou change de catégorie.",
    emptyText: "Change de catégorie ou reviens plus tard pour de nouveaux motifs.",
    openPreview: "Ouvrir l’aperçu",
    loadMore: "Charger {count} motifs de plus",
    visibleCount: "{visible} sur {total} visibles",
    beforePrint: "Avant d’imprimer",
    beforePrintText1:
      "Un bon coloriage a des contours forts, assez d’espace entre les lignes et des motifs que les enfants reconnaissent vite.",
    beforePrintText2:
      "Chaque page de détail montre un aperçu et mène directement au grand fichier PNG pour l’impression A4.",
    printTips: "Conseils d’impression",
    printTip1Title: "Choisir le bon motif",
    printTip1Text:
      "Petits enfants : grandes zones. Enfants plus âgés : scènes, motifs et détails plus riches.",
    printTip2Title: "Imprimer en A4",
    printTip2Text: "Ouvre le fichier PNG, choisis le format portrait et imprime en noir.",
    printTip3Title: "Combiner plusieurs pages",
    printTip3Text:
      "Pour l’école, un anniversaire ou les vacances, un petit dossier thématique est pratique.",
    relatedSearchesTitle: "Coloriages recherchés",
    questions: "Questions",
    faqFreeQuestion: "Les coloriages sont-ils gratuits ?",
    faqFreeAnswer:
      "Oui. Les motifs sur usmalbilder.ch peuvent être ouverts et imprimés gratuitement. Chaque page de détail mène directement au fichier PNG imprimable.",
    faqKidsQuestion: "Quels coloriages conviennent aux enfants ?",
    faqKidsAnswer:
      "Les motifs avec contours nets, grandes zones et peu de petits détails conviennent particulièrement bien.",
    faqPrintQuestion: "Comment imprimer un coloriage ?",
    faqPrintAnswer:
      "Ouvre un motif, choisis le fichier original et imprime-le en A4 au format portrait. Pour des lignes nettes, utilise l’option d’ajustement à la page ou une échelle proche de 100 %.",
    faqTopicsQuestion: "Quels thèmes sont disponibles ?",
    faqTopicsAnswer:
      "La collection contient des licornes, princesses, dinosaures, animaux, mandalas, espace, nature, nourriture, été, Pâques, Noël, Halloween et fantaisie.",
    faqNoLoginQuestion: "Puis-je imprimer les coloriages directement sans inscription ?",
    faqNoLoginAnswer:
      "Oui. Ouvre un motif dans la galerie, consulte sa page détaillée et ouvre directement le fichier imprimable, sans compte ni inscription.",
    faqFormatsQuestion: "Quels formats de fichiers sont disponibles ?",
    faqFormatsAnswer:
      "Chaque page détaillée renvoie vers un PNG facile à imprimer au format A4 portrait. De nombreux thèmes proposent aussi des packs PDF avec plusieurs motifs.",
    faqPdfPacksQuestion: "Existe-t-il des packs PDF avec plusieurs coloriages ?",
    faqPdfPacksAnswer:
      "Oui. La page d’accueil et de nombreuses pages thématiques proposent des packs PDF qui regroupent plusieurs coloriages A4 assortis dans un même fichier.",
    detailLoadingTitle: "Préparation de l’image",
    detailLoadingText:
      "La page de détail charge les données de l’image et prépare la vue d’impression.",
    detailErrorTitle: "Cette page de détail n’est pas disponible pour le moment.",
    detailErrorText: "Recharge la page ou retourne à la galerie.",
    detailMissingTitle: "Ce coloriage est introuvable.",
    detailMissingText:
      "Le fichier a peut-être été déplacé ou la galerie doit être préparée à nouveau.",
    imageMissingPageTitle: "Coloriage introuvable | usmalbilder.ch",
    imageLoadingPageTitle: "Coloriage en cours de chargement | usmalbilder.ch",
    imagePageTitle: "{title} - impression A4 gratuite | usmalbilder.ch",
    topicHubPageTitle: "{title} | coloriages gratuits | usmalbilder.ch",
    imagePageDescription:
      "{description} Ouvre ce coloriage gratuit en PNG, imprime-le en A4 et utilise-le comme page à colorier pour enfants.",
    freeColoringPages: "Coloriages gratuits et pages à imprimer sur usmalbilder.ch.",
    backToGallery: "Retour à la galerie",
    free: "Gratuit",
    instantPrint: "Impression directe",
    noLogin: "Sans inscription",
    printFormats: "A4 PNG et PDF",
    openColoringPage: "Ouvrir le coloriage",
    moreFromCollection: "Plus dans cette collection",
    imageDetails: "Détails de l’image",
    asFreeTemplate: "{title} comme page gratuite à imprimer",
    detailText:
      "Ce coloriage {category} est préparé sous forme de fichier PNG facile à imprimer. Ouvre-le, imprime-le en A4 et utilise-le à la maison, à la maternelle ou à l’école.",
    category: "Catégorie",
    file: "Fichier",
    use: "Usage",
    useValue: "Maison, école ou pause créative",
    sameCollection: "Dans la même collection",
    imageRelatedTopicsTitle: "Thèmes associés",
    curatedImagesTitle: "Coloriages recommandés",
    ageGuideTitle: "Âge conseillé",
    difficultyGuideTitle: "Niveau de difficulté",
    printGuideTitle: "Conseils d'impression",
    bestForTitle: "Particulièrement adapté à",
    imageSupportTitle: "Imprimer et utiliser",
    imageSupportText:
      "Plus d'informations sur la création, l'utilisation et l'impression A4 de ces coloriages.",
    imageHowMadeLink: "Comment les coloriages sont créés",
    imageLicenseLink: "Utilisation et licence",
    imagePrintTipsLink: "Conseils d'impression A4",
    imageKeywordColoringPage: "coloriage",
    imageKeywordPrintable: "page à imprimer",
    notFoundTitle: "Cette page n’existe pas.",
    notFoundText:
      "Le lien est probablement ancien ou la route a changé. La galerie te ramène rapidement aux coloriages.",
    aboutTitle: "À propos de usmalbilder.ch",
    aboutMetaTitle: "À propos du projet | usmalbilder.ch",
    aboutDescription:
      "En savoir plus sur le projet familial derrière usmalbilder.ch et l’usage de l’IA pour des coloriages modernes.",
    aboutBreadcrumb: "À propos du projet",
    aboutEyebrow: "Derrière le projet",
    aboutHeroText:
      "usmalbilder.ch est un projet familial autour de coloriages modernes et faciles à imprimer. Les motifs sont créés avec l’aide de l’IA, puis préparés, triés et publiés pour le site.",
    aboutPrimary: "Trouver des coloriages",
    aboutSecondary: "En savoir plus",
    aboutLead:
      "L’idée est volontairement simple : de bons coloriages doivent être faciles à trouver, sans catalogues confus, publicités agressives ni téléchargements compliqués.",
    aboutParagraph1:
      "L’IA générative est utilisée ici comme un outil pour créer de nouvelles illustrations pour enfants et adultes. Les images sont ensuite sélectionnées, titrées, décrites, rangées dans des collections et préparées pour l’impression.",
    aboutParagraph2:
      "Le projet est aussi une expérience d’apprentissage concrète : comment utiliser intelligemment des outils numériques, construire sa propre offre en ligne et en faire une petite activité familiale ?",
    aboutParagraph3:
      "Si tu veux en savoir plus sur le contexte, tu trouveras d’autres informations sur christianmenz.ch.",
    aboutPrinciple1Label: "Idée",
    aboutPrinciple1Title: "Un petit projet familial",
    aboutPrinciple1Text:
      "Le site associe créativité, outils numériques et plaisir de construire quelque chose à soi.",
    aboutPrinciple2Label: "Approche",
    aboutPrinciple2Title: "L’IA comme outil, pas comme but",
    aboutPrinciple2Text:
      "Les modèles génératifs aident à créer de nouveaux motifs qui sont ensuite sélectionnés et publiés sur le site.",
    aboutPrinciple3Label: "Objectif",
    aboutPrinciple3Title: "Simple pour parents et enfants",
    aboutPrinciple3Text:
      "Les coloriages doivent être rapides à trouver, ouverts directement et imprimés sans détour.",
    whyTitle: "Pourquoi les coloriages font du bien",
    whyMetaTitle: "Pourquoi les coloriages font du bien | usmalbilder.ch",
    whyDescription:
      "Pourquoi les coloriages favorisent la concentration, la détente et l’expression créative chez les enfants et les adultes.",
    whyEyebrow: "Pause créative",
    whyHeroText:
      "Colorier est plus qu’une simple occupation. Cela favorise la concentration, la détente et un accès simple à l’expression créative pour enfants et adultes.",
    whyLead:
      "Les effets positifs du coloriage sur le bien-être sont étonnamment variés. Quelques minutes peuvent aider à mettre le quotidien de côté et à retrouver de la clarté.",
    whyParagraph1:
      "Colorier combine une tâche claire avec beaucoup de liberté. Il est donc facile de commencer, sans pression ni impression de devoir réussir quelque chose.",
    whyParagraph2:
      "C’est précieux pour les enfants, qui découvrent les couleurs, les formes et la concentration en jouant. Les adultes profitent souvent de la répétition calme, presque méditative.",
    whyInfoLabel: "En bref",
    whyInfoTitle: "Moins de stimulation, plus de calme",
    whyInfoText:
      "Les coloriages offrent une entrée simple dans une pause concentrée, sans préparation.",
    benefit1Title: "Focalisation et concentration",
    benefit1Text:
      "Le coloriage rassemble l’attention. Il aide enfants et adultes à rester un moment sur une seule chose.",
    benefit2Title: "Réduction du stress",
    benefit2Text:
      "Des gestes calmes et répétés créent une routine détendue et peuvent ralentir les pensées qui tournent en boucle.",
    benefit3Title: "Expression créative",
    benefit3Text:
      "Couleurs, contrastes et choix personnels transforment chaque page en petit dessin unique.",
    benefit4Title: "Pause attentive",
    benefit4Text:
      "Colorier offre un contrepoids aux écrans rapides et ressemble souvent à une petite pause créative.",
  },
  it: {
    siteTagline: "Disegni da colorare gratuiti con stile",
    navGallery: "Galleria",
    navTopics: "Temi",
    navPrint: "Stampa",
    navFaq: "FAQ",
    languageLabel: "Lingua",
    footerCopy: "Disegni da colorare gratuiti e pagine da stampare.",
    footerSubcopy:
      "Animali, dinosauri, unicorni, natura, feste e motivi stagionali in PNG facili da stampare.",
    footerAnimals: "Animali",
    footerDinosaurs: "Dinosauri",
    footerUnicorn: "Unicorno",
    footerEaster: "Pasqua",
    footerAbout: "Info",
    footerHowMade: "Creazione",
    footerLicense: "Uso",
    footerPrintTips: "Stampa",
    footerPrivacy: "Privacy",
    privacySettings: "Impostazioni cookie",
    homeTitle: "Disegni da colorare gratuiti da stampare",
    homePageTitle: "Disegni da colorare gratuiti da stampare | usmalbilder.ch",
    categoryPageTitle: "Disegni {category} da stampare | usmalbilder.ch",
    categoryMissingTitle: "Categoria non trovata | usmalbilder.ch",
    homeDescription:
      "Oltre 2.000 disegni da colorare gratis per bambini: animali, dinosauri, unicorni, stagioni e altri motivi da stampare in PNG o PDF.",
    categoryDescription:
      "Disegni {category} da stampare: pagine di qualità con contorni chiari, anteprima veloce e file PNG diretti.",
    categoryMissingDescription: "Questa categoria di disegni da colorare non è stata trovata.",
    homeIntro:
      "Stampa disegni gratuiti direttamente, senza registrazione e senza passaggi inutili. Apri il tuo motivo preferito in PNG o PDF A4 e inizia subito. Ogni pagina ha linee nere chiare, sfondo luminoso e spazi facili da colorare per casa, scuola dell’infanzia e scuola.",
    categoryIntro:
      "I disegni {category} sono ideali quando serve subito un motivo pronto da stampare per casa, scuola, asilo o una pausa creativa.",
    searchLabel: "Cerca un disegno",
    searchAria: "Cerca disegni da colorare",
    searchPlaceholder: "Unicorno, dino, Pasqua, razzo...",
    searchHintsAria: "Ricerche popolari",
    heroPromisesAria: "Vantaggi dei disegni",
    heroShowcaseAria: "Esempi di disegni",
    statsAria: "Statistiche galleria",
    statImages: "disegni",
    statPremium: "nuovi motivi premium",
    statCategories: "mondi tematici",
    statPrint: "PNG da stampare",
    all: "Tutti",
    featuredTopics: "Temi popolari",
    ageGroupsTitle: "Disegni per età",
    ageGroupsText:
      "Scegli una raccolta con un livello di dettaglio adatto all'età e alla voglia di colorare.",
    ageGroupYoungTitle: "2-4 anni",
    ageGroupYoungText: "Animali dalle forme chiare e riconoscibili per iniziare facilmente.",
    ageGroupMiddleTitle: "4-6 anni",
    ageGroupMiddleText: "Personaggi fantastici familiari e piccole scene per scuola dell'infanzia.",
    ageGroupOlderTitle: "7+ anni",
    ageGroupOlderText: "Motivi e disegni più dettagliati per pause creative più lunghe.",
    newestImagesTitle: "Disegni aggiunti di recente",
    newestImagesText: "Nuovi motivi facili da stampare dalle raccolte ampliate più di recente.",
    categoriesTitle: "Disegni per tema",
    popularImages: "Disegni popolari",
    topicHubsTitle: "Pagine tematiche per ricerche popolari",
    topicHubsText:
      "Ingressi diretti per disegni gratis, disegni da stampare e unicorno da colorare.",
    pdfPacksTitle: "Pacchetti PDF da stampare",
    pdfPacksText:
      "Scarica più disegni coordinati in un PDF A4. I pacchetti sono generati automaticamente dalle immagini.",
    downloadPdfPack: "Scarica PDF",
    topicCount: "{count} disegni",
    allImages: "Tutti i disegni",
    total: "{count} totali",
    galleryErrorTitle: "La galleria non può essere caricata al momento.",
    galleryErrorText:
      "Ricarica la pagina. Se il problema rimane, controlla che public/db.json sia stato generato.",
    missingCategoryTitle: "Questa categoria non esiste.",
    noSearchTitle: "Nessun risultato per questa ricerca.",
    emptyTitle: "Non ci sono ancora immagini in questa raccolta.",
    missingCategoryText: "Scegli una categoria esistente dalla galleria.",
    noSearchText: "Prova un termine più breve o passa a un’altra categoria.",
    emptyText: "Cambia categoria o torna più tardi per nuovi motivi.",
    openPreview: "Apri anteprima",
    loadMore: "Carica altri {count} motivi",
    visibleCount: "{visible} di {total} visibili",
    beforePrint: "Prima di stampare",
    beforePrintText1:
      "Un buon disegno ha contorni forti, abbastanza spazio tra le linee e motivi che i bambini riconoscono subito.",
    beforePrintText2:
      "Ogni pagina di dettaglio mostra un’anteprima e porta direttamente al file PNG grande per la stampa A4.",
    printTips: "Consigli di stampa",
    printTip1Title: "Scegliere il motivo giusto",
    printTip1Text:
      "Bambini piccoli: grandi aree. Bambini più grandi: scene, pattern e motivi più dettagliati.",
    printTip2Title: "Stampare in A4",
    printTip2Text: "Apri il file PNG, scegli il formato verticale e stampa in nero.",
    printTip3Title: "Combinare più pagine",
    printTip3Text:
      "Per scuola, compleanni o vacanze, una piccola cartella tematica è utile.",
    relatedSearchesTitle: "Disegni cercati",
    questions: "Domande",
    faqFreeQuestion: "I disegni sono gratuiti?",
    faqFreeAnswer:
      "Sì. I motivi su usmalbilder.ch possono essere aperti e stampati gratis. Ogni pagina di dettaglio porta direttamente al PNG stampabile.",
    faqKidsQuestion: "Quali disegni sono adatti ai bambini?",
    faqKidsAnswer:
      "I motivi con contorni chiari, grandi aree e pochi dettagli piccoli sono particolarmente adatti.",
    faqPrintQuestion: "Come stampo un disegno?",
    faqPrintAnswer:
      "Apri un motivo, scegli il file originale e stampalo in A4 in formato verticale. Per linee pulite usa 'adatta alla pagina' o una scala vicina al 100 percento.",
    faqTopicsQuestion: "Quali temi ci sono?",
    faqTopicsAnswer:
      "La raccolta contiene unicorni, principesse, dinosauri, animali, mandala, spazio, natura, cibo, estate, Pasqua, Natale, Halloween e fantasia.",
    faqNoLoginQuestion: "Posso stampare i disegni direttamente senza registrarmi?",
    faqNoLoginAnswer:
      "Sì. Apri un motivo nella galleria, visita la pagina del dettaglio e apri direttamente il file stampabile, senza account né registrazione.",
    faqFormatsQuestion: "Quali formati di file sono disponibili?",
    faqFormatsAnswer:
      "Ogni pagina del dettaglio rimanda a un PNG facile da stampare in formato A4 verticale. Molti temi offrono anche pacchetti PDF con più motivi.",
    faqPdfPacksQuestion: "Ci sono pacchetti PDF con più disegni?",
    faqPdfPacksAnswer:
      "Sì. La pagina iniziale e molte pagine tematiche offrono pacchetti PDF che raccolgono più disegni A4 coordinati in un unico file.",
    detailLoadingTitle: "Preparazione immagine",
    detailLoadingText:
      "La pagina di dettaglio carica i dati dell’immagine e prepara la vista di stampa.",
    detailErrorTitle: "Questa pagina di dettaglio non è disponibile al momento.",
    detailErrorText: "Ricarica la pagina o torna alla galleria.",
    detailMissingTitle: "Questo disegno non è stato trovato.",
    detailMissingText:
      "Il file potrebbe essere stato spostato o la galleria deve essere preparata di nuovo.",
    imageMissingPageTitle: "Disegno non trovato | usmalbilder.ch",
    imageLoadingPageTitle: "Disegno in caricamento | usmalbilder.ch",
    imagePageTitle: "{title} - stampa A4 gratuita | usmalbilder.ch",
    topicHubPageTitle: "{title} | disegni gratuiti | usmalbilder.ch",
    imagePageDescription:
      "{description} Apri questo disegno gratuito come PNG, stampalo in A4 e usalo come pagina da colorare per bambini.",
    freeColoringPages: "Disegni da colorare gratuiti su usmalbilder.ch.",
    backToGallery: "Torna alla galleria",
    free: "Gratis",
    instantPrint: "Stampa diretta",
    noLogin: "Senza registrazione",
    printFormats: "A4 PNG e PDF",
    openColoringPage: "Apri disegno",
    moreFromCollection: "Altri in questa raccolta",
    imageDetails: "Dettagli immagine",
    asFreeTemplate: "{title} come pagina gratuita da stampare",
    detailText:
      "Questo disegno {category} è preparato come file PNG facile da stampare. Aprilo, stampalo in A4 e usalo a casa, all’asilo o a scuola.",
    category: "Categoria",
    file: "File",
    use: "Uso",
    useValue: "Casa, scuola o pausa creativa",
    sameCollection: "Dalla stessa raccolta",
    imageRelatedTopicsTitle: "Temi correlati",
    curatedImagesTitle: "Disegni consigliati",
    ageGuideTitle: "Età consigliata",
    difficultyGuideTitle: "Livello di difficoltà",
    printGuideTitle: "Consigli di stampa",
    bestForTitle: "Particolarmente adatto per",
    imageSupportTitle: "Stampare e usare",
    imageSupportText:
      "Altre informazioni sulla creazione, l'uso e la stampa A4 di questi disegni.",
    imageHowMadeLink: "Come nascono i disegni",
    imageLicenseLink: "Uso e licenza",
    imagePrintTipsLink: "Consigli di stampa A4",
    imageKeywordColoringPage: "disegno da colorare",
    imageKeywordPrintable: "pagina da stampare",
    notFoundTitle: "Questa pagina non esiste.",
    notFoundText:
      "Il link è probabilmente vecchio o la rotta è cambiata. La galleria ti riporta rapidamente ai disegni.",
    aboutTitle: "Chi siamo: usmalbilder.ch",
    aboutMetaTitle: "Informazioni sul progetto | usmalbilder.ch",
    aboutDescription:
      "Scopri il progetto familiare dietro usmalbilder.ch e l’uso dell’IA per disegni da colorare moderni.",
    aboutBreadcrumb: "Informazioni sul progetto",
    aboutEyebrow: "Dietro il progetto",
    aboutHeroText:
      "usmalbilder.ch è un progetto familiare dedicato a disegni moderni e facili da stampare. I motivi nascono con il supporto dell’IA e poi vengono preparati, ordinati e pubblicati sul sito.",
    aboutPrimary: "Trova disegni",
    aboutSecondary: "Scopri di più",
    aboutLead:
      "L’idea è volutamente semplice: buone pagine da colorare devono essere facili da trovare, senza cataloghi confusi, pubblicità aggressiva o download complicati.",
    aboutParagraph1:
      "Qui l’IA generativa viene usata come strumento per sviluppare nuove illustrazioni per bambini e adulti. Le immagini vengono poi selezionate, titolate, descritte, inserite nelle raccolte e preparate per la stampa.",
    aboutParagraph2:
      "Allo stesso tempo, il progetto è anche un’esperienza pratica: come usare bene gli strumenti digitali, costruire una propria offerta online e trasformarla in una piccola attività familiare?",
    aboutParagraph3:
      "Se vuoi saperne di più sul contesto, trovi altre informazioni su christianmenz.ch.",
    aboutPrinciple1Label: "Idea",
    aboutPrinciple1Title: "Un piccolo progetto familiare",
    aboutPrinciple1Text:
      "Il sito unisce creatività, strumenti digitali e il piacere di costruire qualcosa di proprio.",
    aboutPrinciple2Label: "Approccio",
    aboutPrinciple2Title: "IA come strumento, non come fine",
    aboutPrinciple2Text:
      "I modelli generativi aiutano a creare nuovi motivi che poi vengono selezionati e pubblicati sul sito.",
    aboutPrinciple3Label: "Obiettivo",
    aboutPrinciple3Title: "Semplice per genitori e bambini",
    aboutPrinciple3Text:
      "Le pagine da colorare devono essere veloci da trovare, aprirsi direttamente e stamparsi senza passaggi inutili.",
    whyTitle: "Perché colorare fa bene",
    whyMetaTitle: "Perché colorare fa bene | usmalbilder.ch",
    whyDescription:
      "Perché i disegni da colorare favoriscono concentrazione, relax ed espressione creativa per bambini e adulti.",
    whyEyebrow: "Pausa creativa",
    whyHeroText:
      "Colorare è più di un passatempo. Crea concentrazione, relax e un accesso semplice all’espressione creativa per bambini e adulti.",
    whyLead:
      "Gli effetti positivi del colorare sul benessere sono sorprendentemente vari. Bastano pochi minuti per mettere da parte la giornata e riordinare i pensieri.",
    whyParagraph1:
      "Colorare combina un compito chiaro con molta libertà. È facile iniziare subito, senza pressione e senza la sensazione di dover dimostrare qualcosa.",
    whyParagraph2:
      "Per i bambini è prezioso perché vivono colori, forme e concentrazione giocando. Gli adulti spesso traggono beneficio dalla ripetizione calma, quasi meditativa.",
    whyInfoLabel: "In breve",
    whyInfoTitle: "Meno stimoli, più calma",
    whyInfoText:
      "Le pagine da colorare offrono un ingresso semplice in una pausa concentrata, senza preparazione.",
    benefit1Title: "Focus e concentrazione",
    benefit1Text:
      "Colorare raccoglie l’attenzione. Aiuta bambini e adulti a restare per un momento su una sola cosa.",
    benefit2Title: "Riduzione dello stress",
    benefit2Text:
      "Movimenti calmi e ripetuti creano una routine rilassata e possono rallentare i pensieri che corrono.",
    benefit3Title: "Espressione creativa",
    benefit3Text:
      "Colori, contrasti e scelte personali trasformano ogni pagina in una piccola opera propria.",
    benefit4Title: "Pausa consapevole",
    benefit4Text:
      "Colorare crea un contrappeso agli schermi veloci e spesso sembra una piccola pausa creativa.",
  },
  es: {
    siteTagline: "Dibujos para colorear gratis con estilo",
    navGallery: "Galería",
    navTopics: "Temas",
    navPrint: "Imprimir",
    navFaq: "FAQ",
    languageLabel: "Idioma",
    footerCopy: "Dibujos para colorear gratis y páginas para imprimir.",
    footerSubcopy:
      "Animales, dinosaurios, unicornios, naturaleza, fiestas y motivos de temporada en archivos PNG fáciles de imprimir.",
    footerAnimals: "Animales",
    footerDinosaurs: "Dinosaurios",
    footerUnicorn: "Unicornio",
    footerEaster: "Pascua",
    footerAbout: "Sobre",
    footerHowMade: "Creación",
    footerLicense: "Uso",
    footerPrintTips: "Impresión",
    footerPrivacy: "Privacidad",
    privacySettings: "Configuración de cookies",
    homeTitle: "Dibujos para colorear gratis para imprimir",
    homePageTitle: "Dibujos para colorear gratis para imprimir | usmalbilder.ch",
    categoryPageTitle: "Dibujos de {category} para imprimir | usmalbilder.ch",
    categoryMissingTitle: "Categoría no encontrada | usmalbilder.ch",
    homeDescription:
      "Más de 2.000 dibujos para colorear gratis: animales, dinosaurios, unicornios, estaciones y más para imprimir en PNG o PDF.",
    categoryDescription:
      "Dibujos de {category} para imprimir: páginas de calidad con contornos claros, vista previa rápida y archivos PNG directos.",
    categoryMissingDescription: "Esta categoría de dibujos para colorear no se encontró.",
    homeIntro:
      "Imprime dibujos gratuitos directamente, sin registro ni rodeos. Abre tu motivo favorito como PNG o PDF A4 y empieza enseguida. Todas las páginas tienen líneas negras claras, fondo limpio y espacios fáciles de colorear para casa, infantil y colegio.",
    categoryIntro:
      "Los dibujos de {category} son ideales cuando necesitas un motivo listo para imprimir en casa, el jardín de infancia, la escuela o una pausa creativa tranquila.",
    searchLabel: "Buscar dibujos",
    searchAria: "Buscar dibujos para colorear",
    searchPlaceholder: "Unicornio, dino, Pascua, cohete...",
    searchHintsAria: "Búsquedas populares",
    heroPromisesAria: "Ventajas de los dibujos",
    heroShowcaseAria: "Ejemplos de dibujos para colorear",
    statsAria: "Estadísticas de la galería",
    statImages: "dibujos",
    statPremium: "nuevos motivos premium",
    statCategories: "mundos temáticos",
    statPrint: "PNG para imprimir",
    all: "Todos",
    featuredTopics: "Temas populares",
    ageGroupsTitle: "Dibujos por edad",
    ageGroupsText:
      "Elige una colección con un nivel de detalle adecuado para la edad y las ganas de colorear.",
    ageGroupYoungTitle: "2-4 años",
    ageGroupYoungText: "Animales con formas claras y fáciles de reconocer para empezar.",
    ageGroupMiddleTitle: "4-6 años",
    ageGroupMiddleText: "Personajes de fantasía conocidos y pequeñas escenas para infantil.",
    ageGroupOlderTitle: "7+ años",
    ageGroupOlderText: "Patrones y dibujos con más detalles para pausas creativas largas.",
    newestImagesTitle: "Dibujos añadidos recientemente",
    newestImagesText: "Motivos nuevos y fáciles de imprimir de las colecciones ampliadas recientemente.",
    categoriesTitle: "Dibujos por tema",
    popularImages: "Dibujos populares",
    topicHubsTitle: "Páginas temáticas para búsquedas populares",
    topicHubsText:
      "Entradas directas para dibujos gratis, dibujos para imprimir y unicornio para colorear.",
    pdfPacksTitle: "Paquetes PDF para imprimir",
    pdfPacksText:
      "Descarga varios dibujos relacionados en un PDF A4. Los paquetes se generan automáticamente desde las imágenes.",
    downloadPdfPack: "Descargar PDF",
    topicCount: "{count} dibujos",
    allImages: "Todos los dibujos",
    total: "{count} en total",
    galleryErrorTitle: "La galería no se puede cargar ahora mismo.",
    galleryErrorText:
      "Recarga la página. Si el problema continúa, comprueba si public/db.json fue generado.",
    missingCategoryTitle: "Esta categoría no existe.",
    noSearchTitle: "No hay resultados para esta búsqueda.",
    emptyTitle: "Todavía no hay imágenes en esta colección.",
    missingCategoryText: "Elige una categoría existente de la galería.",
    noSearchText: "Prueba un término más corto o cambia de categoría.",
    emptyText: "Cambia de categoría o vuelve más tarde para ver nuevos motivos.",
    openPreview: "Abrir vista previa",
    loadMore: "Cargar {count} motivos más",
    visibleCount: "{visible} de {total} visibles",
    beforePrint: "Antes de imprimir",
    beforePrintText1:
      "Un buen dibujo para colorear tiene contornos fuertes, suficiente espacio entre las líneas y motivos que los niños reconocen enseguida.",
    beforePrintText2:
      "Cada página de detalle muestra una vista previa y enlaza directamente al archivo PNG grande para imprimir en A4.",
    printTips: "Consejos de impresión",
    printTip1Title: "Elegir el motivo adecuado",
    printTip1Text:
      "Niños pequeños: zonas grandes. Niños mayores: escenas, patrones y motivos con más detalles.",
    printTip2Title: "Imprimir en A4",
    printTip2Text: "Abre el archivo PNG, elige formato vertical e imprime con tinta negra.",
    printTip3Title: "Combinar varias páginas",
    printTip3Text:
      "Para la escuela, cumpleaños o vacaciones, una pequeña carpeta temática resulta práctica.",
    relatedSearchesTitle: "Dibujos buscados",
    questions: "Preguntas",
    faqFreeQuestion: "¿Los dibujos son gratis?",
    faqFreeAnswer:
      "Sí. Los motivos de usmalbilder.ch se pueden abrir e imprimir gratis. Cada página de detalle enlaza directamente al archivo PNG imprimible.",
    faqKidsQuestion: "¿Qué dibujos son adecuados para niños?",
    faqKidsAnswer:
      "Los motivos con contornos claros, zonas grandes y pocos detalles pequeños funcionan especialmente bien.",
    faqPrintQuestion: "¿Cómo imprimo un dibujo?",
    faqPrintAnswer:
      "Abre un motivo, elige el archivo original e imprímelo en A4 en formato vertical. Para líneas limpias, usa 'ajustar a la página' o una escala cercana al 100 por ciento.",
    faqTopicsQuestion: "¿Qué temas hay?",
    faqTopicsAnswer:
      "La colección incluye unicornios, princesas, dinosaurios, animales, mandalas, espacio, naturaleza, comida, verano, Pascua, Navidad, Halloween y fantasía.",
    faqNoLoginQuestion: "¿Puedo imprimir los dibujos directamente sin registrarme?",
    faqNoLoginAnswer:
      "Sí. Abre un motivo en la galería, visita su página de detalle y abre el archivo imprimible directamente, sin cuenta ni registro.",
    faqFormatsQuestion: "¿Qué formatos de archivo están disponibles?",
    faqFormatsAnswer:
      "Cada página de detalle enlaza a un PNG fácil de imprimir en formato A4 vertical. Muchos temas también ofrecen paquetes PDF con varios motivos.",
    faqPdfPacksQuestion: "¿Hay paquetes PDF con varios dibujos?",
    faqPdfPacksAnswer:
      "Sí. La página de inicio y muchas páginas temáticas ofrecen paquetes PDF que reúnen varios dibujos A4 relacionados en un solo archivo.",
    detailLoadingTitle: "Preparando imagen",
    detailLoadingText:
      "La vista de detalle está cargando los datos de la imagen y preparando la vista de impresión.",
    detailErrorTitle: "Esta página de detalle no está disponible ahora mismo.",
    detailErrorText: "Recarga la página o vuelve a la galería.",
    detailMissingTitle: "Este dibujo para colorear no se encontró.",
    detailMissingText:
      "Puede que el archivo se haya movido o que la galería deba prepararse de nuevo.",
    imageMissingPageTitle: "Dibujo no encontrado | usmalbilder.ch",
    imageLoadingPageTitle: "Cargando dibujo | usmalbilder.ch",
    imagePageTitle: "{title} - impresión A4 gratis | usmalbilder.ch",
    topicHubPageTitle: "{title} | dibujos gratis | usmalbilder.ch",
    imagePageDescription:
      "{description} Abre este dibujo gratuito como PNG, imprímelo en A4 y úsalo como página para colorear para niños.",
    freeColoringPages: "Dibujos para colorear gratis en usmalbilder.ch.",
    backToGallery: "Volver a la galería",
    free: "Gratis",
    instantPrint: "Impresión directa",
    noLogin: "Sin registro",
    printFormats: "A4 PNG y PDF",
    openColoringPage: "Abrir dibujo",
    moreFromCollection: "Más de esta colección",
    imageDetails: "Detalles de la imagen",
    asFreeTemplate: "{title} como página gratuita para imprimir",
    detailText:
      "Este dibujo de {category} está preparado como archivo PNG fácil de imprimir. Ábrelo, imprímelo en A4 y úsalo en casa, en el jardín de infancia o en la escuela.",
    category: "Categoría",
    file: "Archivo",
    use: "Uso",
    useValue: "Casa, escuela o pausa creativa",
    sameCollection: "De la misma colección",
    imageRelatedTopicsTitle: "Temas relacionados",
    curatedImagesTitle: "Dibujos recomendados",
    ageGuideTitle: "Edad recomendada",
    difficultyGuideTitle: "Nivel de dificultad",
    printGuideTitle: "Consejos de impresión",
    bestForTitle: "Especialmente adecuado para",
    imageSupportTitle: "Imprimir y usar",
    imageSupportText:
      "Más información sobre la creación, el uso y la impresión A4 de estos dibujos.",
    imageHowMadeLink: "Cómo se crean los dibujos",
    imageLicenseLink: "Uso y licencia",
    imagePrintTipsLink: "Consejos de impresión A4",
    imageKeywordColoringPage: "dibujo para colorear",
    imageKeywordPrintable: "página para imprimir",
    notFoundTitle: "Esta página no existe.",
    notFoundText:
      "El enlace probablemente está desactualizado o la ruta cambió. La galería te lleva rápidamente de vuelta a los dibujos.",
    aboutTitle: "Sobre usmalbilder.ch",
    aboutMetaTitle: "Sobre el proyecto | usmalbilder.ch",
    aboutDescription:
      "Conoce el proyecto familiar detrás de usmalbilder.ch y el uso de IA para dibujos modernos para colorear.",
    aboutBreadcrumb: "Sobre el proyecto",
    aboutEyebrow: "Detrás del proyecto",
    aboutHeroText:
      "usmalbilder.ch es un proyecto familiar dedicado a dibujos modernos y fáciles de imprimir. Los motivos se crean con apoyo de IA y después se preparan, ordenan y publican en el sitio.",
    aboutPrimary: "Encontrar dibujos",
    aboutSecondary: "Más sobre el proyecto",
    aboutLead:
      "La idea es sencilla a propósito: las buenas páginas para colorear deben encontrarse rápido, sin catálogos confusos, publicidad agresiva ni descargas complicadas.",
    aboutParagraph1:
      "Aquí la IA generativa se usa como herramienta para crear nuevas ilustraciones para niños y adultos. Después las imágenes se revisan, reciben títulos y descripciones, se organizan en colecciones y se preparan para imprimir.",
    aboutParagraph2:
      "Al mismo tiempo, el proyecto es una experiencia práctica: cómo usar bien las herramientas digitales, construir una oferta propia en línea y convertirla en una pequeña actividad familiar.",
    aboutParagraph3:
      "Si quieres saber más sobre el contexto, encontrarás más información en christianmenz.ch.",
    aboutPrinciple1Label: "Idea",
    aboutPrinciple1Title: "Un pequeño proyecto familiar",
    aboutPrinciple1Text:
      "El sitio combina creatividad, herramientas digitales y el placer de construir algo propio.",
    aboutPrinciple2Label: "Enfoque",
    aboutPrinciple2Title: "IA como herramienta, no como fin",
    aboutPrinciple2Text:
      "Los modelos generativos ayudan a crear nuevos motivos que después se revisan y se publican en el sitio.",
    aboutPrinciple3Label: "Objetivo",
    aboutPrinciple3Title: "Fácil para padres y niños",
    aboutPrinciple3Text:
      "Las páginas para colorear deben encontrarse rápido, abrirse directamente e imprimirse sin pasos innecesarios.",
    whyTitle: "Por qué colorear es bueno",
    whyMetaTitle: "Por qué colorear es bueno | usmalbilder.ch",
    whyDescription:
      "Por qué los dibujos para colorear favorecen la concentración, la relajación y la expresión creativa en niños y adultos.",
    whyEyebrow: "Pausa creativa",
    whyHeroText:
      "Colorear es más que una actividad para pasar el rato. Ayuda a concentrarse, relajarse y acceder de forma sencilla a la expresión creativa.",
    whyLead:
      "Los efectos positivos de colorear en el bienestar son sorprendentemente variados. Unos pocos minutos pueden ayudar a dejar atrás el ruido diario y ordenar la mente.",
    whyParagraph1:
      "Colorear combina una tarea clara con mucha libertad. Por eso es fácil empezar sin presión y sin sentir que hay que demostrar nada.",
    whyParagraph2:
      "Para los niños es valioso porque experimentan colores, formas y concentración jugando. Los adultos suelen beneficiarse de la repetición tranquila, casi meditativa.",
    whyInfoLabel: "En resumen",
    whyInfoTitle: "Menos estímulos, más calma",
    whyInfoText:
      "Las páginas para colorear ofrecen una entrada sencilla a una pausa concentrada, sin preparación.",
    benefit1Title: "Foco y concentración",
    benefit1Text:
      "Colorear reúne la atención. Ayuda a niños y adultos a permanecer un momento con una sola cosa.",
    benefit2Title: "Reducción del estrés",
    benefit2Text:
      "Los movimientos tranquilos y repetidos crean una rutina relajada y pueden frenar los pensamientos acelerados.",
    benefit3Title: "Expresión creativa",
    benefit3Text:
      "Los colores, contrastes y decisiones personales convierten cada página en una pequeña obra propia.",
    benefit4Title: "Pausa consciente",
    benefit4Text:
      "Colorear crea un contrapunto a las pantallas rápidas y a menudo se siente como una pequeña pausa creativa.",
  },
};

const DICTIONARY = {
  ...BASE_DICTIONARY,
  ...(localeExtensions.dictionary || {}),
};

const SEARCH_HINTS = {
  de: [
    "Einhorn",
    "Prinzessin",
    "Dinosaurier",
    "Tiere",
    "Ostern",
    "Weihnachten",
    "Weltraum",
    "Sommer",
    "Natur",
    "Halloween",
    "Mandalas",
    "Malvorlagen",
  ],
  en: [
    "Unicorn",
    "Princess",
    "Dinosaurs",
    "Animals",
    "Easter",
    "Christmas",
    "Space",
    "Summer",
    "Nature",
    "Halloween",
    "Mandalas",
    "Coloring pages",
  ],
  fr: [
    "Licorne",
    "Princesse",
    "Dinosaures",
    "Animaux",
    "Pâques",
    "Noël",
    "Espace",
    "Été",
    "Nature",
    "Halloween",
    "Mandalas",
    "Coloriages",
  ],
  it: [
    "Unicorno",
    "Principessa",
    "Dinosauri",
    "Animali",
    "Pasqua",
    "Natale",
    "Spazio",
    "Estate",
    "Natura",
    "Halloween",
    "Mandala",
    "Disegni",
  ],
  es: [
    "Unicornio",
    "Princesa",
    "Dinosaurios",
    "Animales",
    "Pascua",
    "Navidad",
    "Espacio",
    "Verano",
    "Naturaleza",
    "Halloween",
    "Mandalas",
    "Dibujos",
  ],
  ...(localeExtensions.searchHints || {}),
};

const RELATED_SEARCHES = {
  de: [
    "Ausmalbilder kostenlos",
    "Ausmalbilder zum Ausdrucken",
    "Malvorlagen zum Ausdrucken",
    "Malvorlagen kostenlos",
    "Einhorn Ausmalbilder",
    "Prinzessin Ausmalbilder",
    "Dinosaurier Ausmalbilder",
    "Tiere Ausmalbilder",
    "Einfache Ausmalbilder",
    "Süsse Ausmalbilder",
    "Ausmalbilder Kindergarten",
    "Ausmalbilder Schule",
    "Sommer Ausmalbilder",
    "Ostern Ausmalbilder",
    "Weihnachten Ausmalbilder",
    "Mandalas zum Ausdrucken",
    "Halloween Ausmalbilder",
    "Weltraum Ausmalbilder",
  ],
  en: [
    "Free coloring pages",
    "Printable coloring pages",
    "Coloring sheets to print",
    "Free printable sheets",
    "Unicorn coloring pages",
    "Princess coloring pages",
    "Dinosaur coloring pages",
    "Animal coloring pages",
    "Easy coloring pages",
    "Cute coloring pages",
    "Kindergarten coloring pages",
    "School coloring pages",
    "Summer coloring pages",
    "Easter coloring pages",
    "Christmas coloring pages",
    "Printable mandalas",
    "Halloween coloring pages",
    "Space coloring pages",
  ],
  fr: [
    "Coloriages gratuits",
    "Coloriages à imprimer",
    "Pages à colorier gratuites",
    "Licorne coloriage",
    "Princesse coloriage",
    "Dinosaure coloriage",
    "Animaux coloriage",
    "Coloriages faciles",
    "Coloriages mignons",
    "Coloriages maternelle",
    "Coloriages école",
    "Coloriages été",
    "Coloriages Pâques",
    "Coloriages Noël",
    "Mandalas à imprimer",
    "Coloriages Halloween",
    "Coloriages espace",
  ],
  it: [
    "Disegni da colorare gratis",
    "Disegni da stampare",
    "Pagine da colorare gratis",
    "Unicorno da colorare",
    "Principessa da colorare",
    "Dinosauri da colorare",
    "Animali da colorare",
    "Disegni facili da colorare",
    "Disegni carini da colorare",
    "Disegni per scuola",
    "Disegni estate",
    "Disegni Pasqua",
    "Disegni Natale",
    "Mandala da stampare",
    "Disegni Halloween",
    "Disegni spazio",
  ],
  es: [
    "Dibujos para colorear gratis",
    "Dibujos para imprimir",
    "Páginas para colorear gratis",
    "Unicornio para colorear",
    "Princesa para colorear",
    "Dinosaurios para colorear",
    "Animales para colorear",
    "Dibujos fáciles para colorear",
    "Dibujos bonitos para colorear",
    "Dibujos para escuela",
    "Dibujos de verano",
    "Dibujos de Pascua",
    "Dibujos de Navidad",
    "Mandalas para imprimir",
    "Dibujos de Halloween",
    "Dibujos del espacio",
  ],
  ...(localeExtensions.relatedSearches || {}),
};

export function getBrowserLocale() {
  const browserNavigator =
    typeof navigator !== "undefined"
      ? navigator
      : typeof window !== "undefined"
      ? window.navigator
      : null;

  if (!browserNavigator) {
    return DEFAULT_LOCALE;
  }

  const languages =
    browserNavigator.languages && browserNavigator.languages.length
      ? browserNavigator.languages
      : [browserNavigator.language];

  const matched = languages
    .filter(Boolean)
    .map((language) => language.toLowerCase().slice(0, 2))
    .find((language) => SUPPORTED_LOCALES.includes(language));

  return matched || DEFAULT_LOCALE;
}

export function normalizeLocale(locale) {
  return SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
}

export function translate(locale, key, values = {}) {
  const dictionary = DICTIONARY[normalizeLocale(locale)] || DICTIONARY[DEFAULT_LOCALE];
  const template = dictionary[key] || DICTIONARY[DEFAULT_LOCALE][key] || key;

  return Object.entries(values).reduce(
    (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
    template
  );
}

export function ensureTrailingSlash(path = "/") {
  if (!path || path === "/") {
    return "/";
  }

  const [pathname, suffix = ""] = String(path).split(/(?=[?#])/u, 2);
  const normalizedPath = pathname.endsWith("/") ? pathname : `${pathname}/`;
  return `${normalizedPath}${suffix}`;
}

export function getLocalizedPath(path, locale, options = {}) {
  const targetLocale = normalizeLocale(locale);
  const cleanPath = path || "/";
  const withoutLocale = cleanPath.replace(
    new RegExp(`^/(${SUPPORTED_LOCALES.join("|")})(?=/|$)`),
    ""
  ) || "/";
  const localizedPath = localizeKnownCategoryPath(withoutLocale, targetLocale);

  if (targetLocale === DEFAULT_LOCALE && !options.forceLocalePrefix) {
    return ensureTrailingSlash(localizedPath);
  }

  return ensureTrailingSlash(
    localizedPath === "/" ? `/${targetLocale}` : `/${targetLocale}${localizedPath}`
  );
}

export function createAlternateLinks(pathForLocale) {
  const links = SUPPORTED_LOCALES.map((locale) => ({
    hreflang: HREFLANG_BY_LOCALE[locale] || locale,
    href: pathForLocale(locale),
  }));
  const defaultLink = links.find((link) => link.hreflang === HREFLANG_BY_LOCALE.de);

  return defaultLink
    ? [...links, { hreflang: "x-default", href: defaultLink.href }]
    : links;
}

export function localizeCategory(category, locale) {
  const targetLocale = normalizeLocale(locale);
  const translation = category?.translations?.[targetLocale];

  return {
    ...category,
    name: translation?.name || category?.name,
    description: translation?.description || category?.description,
    slug: getCategorySlug(category, targetLocale),
    path: getCategoryPath(category, targetLocale),
  };
}

export function getCategorySlug(category, locale) {
  const targetLocale = normalizeLocale(locale);

  return (
    category?.slugs?.[targetLocale] ||
    CATEGORY_SLUGS[category?.tag]?.[targetLocale] ||
    category?.tag
  );
}

export function getCategoryPath(category, locale, options) {
  return getLocalizedPath(`/${getCategorySlug(category, locale)}`, locale, options);
}

export function getCategoryByRoute(categories, categorySlug) {
  if (!categorySlug) {
    return null;
  }

  return (
    categories.find((category) => category.tag === categorySlug) ||
    categories.find((category) =>
      Object.values(category.slugs || CATEGORY_SLUGS[category.tag] || {}).includes(
        categorySlug
      )
    ) ||
    null
  );
}

export function localizeImage(image, locale) {
  const translation = image?.translations?.[normalizeLocale(locale)];

  return {
    ...image,
    title: translation?.title || image?.title,
    description: translation?.description || image?.description,
  };
}

export function getDefaultLocale() {
  return DEFAULT_LOCALE;
}

export function getSearchHints(locale) {
  return SEARCH_HINTS[normalizeLocale(locale)] || SEARCH_HINTS[DEFAULT_LOCALE];
}

export function getRelatedSearches(locale, categoryName) {
  const targetLocale = normalizeLocale(locale);

  if (categoryName) {
    const suffixes = {
      de: [
        "Ausmalbilder kostenlos",
        "Malvorlagen zum Ausdrucken",
        "Ausmalbild kostenlos",
        "Bilder für Kinder",
        "Vorlage A4",
      ],
      en: [
        "free coloring pages",
        "printable coloring sheets",
        "coloring page",
        "pictures for kids",
        "A4 template",
      ],
      fr: [
        "coloriages gratuits",
        "pages à imprimer",
        "coloriage gratuit",
        "images pour enfants",
        "modèle A4",
      ],
      it: [
        "disegni gratis",
        "pagine da stampare",
        "disegno da colorare",
        "immagini per bambini",
        "modello A4",
      ],
      es: [
        "dibujos gratis",
        "páginas para imprimir",
        "dibujo para colorear",
        "imágenes para niños",
        "plantilla A4",
      ],
      pt: [
        "desenhos grátis",
        "páginas para imprimir",
        "desenho para colorir",
        "imagens para crianças",
        "modelo A4",
      ],
      hi: [
        "मुफ्त कलरिंग पेज",
        "प्रिंट करने योग्य पेज",
        "कलरिंग पेज",
        "बच्चों के चित्र",
        "A4 टेम्पलेट",
      ],
    };

    return suffixes[targetLocale].map((suffix) => `${categoryName} ${suffix}`);
  }

  return RELATED_SEARCHES[targetLocale] || RELATED_SEARCHES[DEFAULT_LOCALE];
}
